import type { MessageDesignTokens, MessageTokenSections } from '@primeuix/themes/types/message';

// Brand guide Feedback (O3): flat callout on the feedback surface, text in the
// feedback colour; radius-large (the guide pulls former 14px surfaces to 16px).
export const root: MessageTokenSections.Root = {
    borderRadius: '{borderRadius.lg}',
    borderWidth: '0',
    transitionDuration: '{transitionDuration}'
};

export const content: MessageTokenSections.Content = {
    padding: '16px 18px',
    gap: '14px',
    sm: {
        padding: '12px 14px'
    },
    lg: {
        padding: '20px 22px'
    }
};

export const text: MessageTokenSections.Text = {
    fontSize: '0.8125rem',
    fontWeight: '400',
    sm: {
        fontSize: '0.75rem'
    },
    lg: {
        fontSize: '0.875rem'
    }
};

export const icon: MessageTokenSections.Icon = {
    size: '1.125rem',
    sm: {
        size: '1rem'
    },
    lg: {
        size: '1.25rem'
    }
};

export const closeButton: MessageTokenSections.CloseButton = {
    width: '1.75rem',
    height: '1.75rem',
    borderRadius: '{extend.borderRadius.rounded}',
    focusRing: {
        width: '{focusRing.width}',
        style: '{focusRing.style}',
        offset: '{focusRing.offset}'
    }
};

export const closeIcon: MessageTokenSections.CloseIcon = {
    size: '1rem',
    sm: {
        size: '0.875rem'
    },
    lg: {
        size: '1.125rem'
    }
};

export const outlined: MessageTokenSections.Outlined = {
    root: {
        borderWidth: '1px'
    }
};

export const simple: MessageTokenSections.Simple = {
    content: {
        padding: '0'
    }
};

export const colorScheme: MessageTokenSections.ColorScheme = {
    light: {
        // DS callouts: flat tint background, no border, no shadow
        info: {
            background: '{extend.stateinfobg}',
            borderColor: 'transparent',
            color: '{extend.stateinfo}',
            shadow: 'none',
            closeButton: {
                hoverBackground: '{extend.surfacesubtle}',
                focusRing: {
                    color: '{focusRing.color}',
                    shadow: 'none'
                }
            },
            outlined: {
                color: '{extend.stateinfo}',
                borderColor: '{extend.stateinfo}'
            },
            simple: {
                color: '{extend.stateinfo}'
            }
        },
        success: {
            background: '{extend.statesuccessbg}',
            borderColor: 'transparent',
            color: '{extend.statesuccess}',
            shadow: 'none',
            closeButton: {
                hoverBackground: '{extend.surfacesubtle}',
                focusRing: {
                    color: '{focusRing.color}',
                    shadow: 'none'
                }
            },
            outlined: {
                color: '{extend.statesuccess}',
                borderColor: '{extend.statesuccess}'
            },
            simple: {
                color: '{extend.statesuccess}'
            }
        },
        warn: {
            background: '{extend.statewarningbg}',
            borderColor: 'transparent',
            color: '{extend.statewarning}',
            shadow: 'none',
            closeButton: {
                hoverBackground: '{extend.surfacesubtle}',
                focusRing: {
                    color: '{focusRing.color}',
                    shadow: 'none'
                }
            },
            outlined: {
                color: '{extend.statewarning}',
                borderColor: '{extend.statewarning}'
            },
            simple: {
                color: '{extend.statewarning}'
            }
        },
        error: {
            background: '{extend.stateerrorbg}',
            borderColor: 'transparent',
            color: '{extend.stateerror}',
            shadow: 'none',
            closeButton: {
                hoverBackground: '{extend.surfacesubtle}',
                focusRing: {
                    color: '{focusRing.color}',
                    shadow: 'none'
                }
            },
            outlined: {
                color: '{extend.stateerror}',
                borderColor: '{extend.stateerror}'
            },
            simple: {
                color: '{extend.stateerror}'
            }
        },
        secondary: {
            background: '{extend.surfacesubtle}',
            borderColor: 'transparent',
            color: '{text.color}',
            shadow: 'none',
            closeButton: {
                hoverBackground: '{extend.surfacesubtle}',
                focusRing: {
                    color: '{text.mutedColor}',
                    shadow: 'none'
                }
            },
            outlined: {
                color: '{text.mutedColor}',
                borderColor: '{text.mutedColor}'
            },
            simple: {
                color: '{text.mutedColor}'
            }
        },
        contrast: {
            background: '{sand.950}',
            borderColor: 'transparent',
            color: '#FFFFFF',
            shadow: 'none',
            closeButton: {
                hoverBackground: '{extend.surfacesubtle}',
                focusRing: {
                    color: '#FFFFFF',
                    shadow: 'none'
                }
            },
            outlined: {
                color: '{sand.950}',
                borderColor: '{sand.950}'
            },
            simple: {
                color: '{sand.950}'
            }
        }
    },
    dark: {
        info: {
            background: 'color-mix(in srgb, {blue.500}, transparent 84%)',
            borderColor: 'color-mix(in srgb, {blue.700}, transparent 64%)',
            color: '{blue.500}',
            shadow: '0px 4px 8px 0px color-mix(in srgb, {blue.500}, transparent 96%)',
            closeButton: {
                hoverBackground: 'rgba(255, 255, 255, 0.05)',
                focusRing: {
                    color: '{blue.500}',
                    shadow: 'none'
                }
            },
            outlined: {
                color: '{blue.500}',
                borderColor: '{blue.500}'
            },
            simple: {
                color: '{blue.500}'
            }
        },
        success: {
            background: 'color-mix(in srgb, {green.500}, transparent 84%)',
            borderColor: 'color-mix(in srgb, {green.700}, transparent 64%)',
            color: '{green.500}',
            shadow: '0px 4px 8px 0px color-mix(in srgb, {green.500}, transparent 96%)',
            closeButton: {
                hoverBackground: 'rgba(255, 255, 255, 0.05)',
                focusRing: {
                    color: '{green.500}',
                    shadow: 'none'
                }
            },
            outlined: {
                color: '{green.500}',
                borderColor: '{green.500}'
            },
            simple: {
                color: '{green.500}'
            }
        },
        warn: {
            background: 'color-mix(in srgb, {yellow.500}, transparent 84%)',
            borderColor: 'color-mix(in srgb, {yellow.700}, transparent 64%)',
            color: '{yellow.500}',
            shadow: '0px 4px 8px 0px color-mix(in srgb, {yellow.500}, transparent 96%)',
            closeButton: {
                hoverBackground: 'rgba(255, 255, 255, 0.05)',
                focusRing: {
                    color: '{yellow.500}',
                    shadow: 'none'
                }
            },
            outlined: {
                color: '{yellow.500}',
                borderColor: '{yellow.500}'
            },
            simple: {
                color: '{yellow.500}'
            }
        },
        error: {
            background: 'color-mix(in srgb, {red.500}, transparent 84%)',
            borderColor: 'color-mix(in srgb, {red.700}, transparent 64%)',
            color: '{red.500}',
            shadow: '0px 4px 8px 0px color-mix(in srgb, {red.500}, transparent 96%)',
            closeButton: {
                hoverBackground: 'rgba(255, 255, 255, 0.05)',
                focusRing: {
                    color: '{red.500}',
                    shadow: 'none'
                }
            },
            outlined: {
                color: '{red.500}',
                borderColor: '{red.500}'
            },
            simple: {
                color: '{red.500}'
            }
        },
        secondary: {
            background: '{surface.800}',
            borderColor: '{surface.700}',
            color: '{surface.300}',
            shadow: '0px 4px 8px 0px color-mix(in srgb, {surface.500}, transparent 96%)',
            closeButton: {
                hoverBackground: '{surface.700}',
                focusRing: {
                    color: '{surface.300}',
                    shadow: 'none'
                }
            },
            outlined: {
                color: '{surface.400}',
                borderColor: '{surface.400}'
            },
            simple: {
                color: '{surface.400}'
            }
        },
        contrast: {
            background: '{surface.0}',
            borderColor: '{surface.100}',
            color: '{surface.950}',
            shadow: '0px 4px 8px 0px color-mix(in srgb, {surface.950}, transparent 96%)',
            closeButton: {
                hoverBackground: '{surface.100}',
                focusRing: {
                    color: '{surface.950}',
                    shadow: 'none'
                }
            },
            outlined: {
                color: '{surface.0}',
                borderColor: '{surface.0}'
            },
            simple: {
                color: '{surface.0}'
            }
        }
    }
};

export default {
    root,
    content,
    text,
    icon,
    closeButton,
    closeIcon,
    outlined,
    simple,
    colorScheme
} satisfies MessageDesignTokens;
